import axios from "axios";
import { baseURL } from "./url";
import TokenManager from './TokenManager';

export default axios.create({
    baseURL: `${baseURL}`,
});

const getTokenManager = () => TokenManager.getInstance();

const api = axios.create({ baseURL: baseURL });

// Map to track pending requests and cancel duplicates
const pendingRequests = new Map();

const getRequestKey = (config) => {
	const { method, url, params, data } = config;
	return `${method}:${url}:${JSON.stringify(params)}:${JSON.stringify(data)}`;
};

// Request interceptor to attach valid token
api.interceptors.request.use(async (config) => {
	const key = getRequestKey(config);

	if (pendingRequests.has(key)) {
		const previousController = pendingRequests.get(key);
		previousController.abort();
		pendingRequests.delete(key);
	}

	const controller = new AbortController();
	config.signal = controller.signal;
	pendingRequests.set(key, controller);

	try {
		const token = await getTokenManager().getValidToken();

		if (token) {
			config.headers = config.headers || {};
			config.headers['Authorization'] = `Bearer ${token}`;
		}
	} catch (error) {
		console.error('Error fetching valid token:', error);
		throw error;
	}

	return config;
});

// Response interceptor to remove pending requests
api.interceptors.response.use(
	(response) => {
		const key = getRequestKey(response.config);
		pendingRequests.delete(key);
		return response;
	},
	async (error) => {
		const key = getRequestKey(error.config || {});
		pendingRequests.delete(key);

		const originalRequest = error.config;

		// Retry once on 401 using the TokenManager
		if (error.response?.status === 401 && !originalRequest._retry) {
			originalRequest._retry = true;
			try {
				const newToken = await getTokenManager().getValidToken();
				originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
				return api(originalRequest);
			} catch (refreshError) {
				getTokenManager().clearToken();
				throw refreshError;
			}
		}

		throw error;
	},
);
export { api };
